import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import styles from './styles.module.scss';
import {
  MetricChartDotRadius,
  MetricChartDotSmall,
  MetricChartXAxisHeight,
  MetricChartYAxisWidth,
} from './constants/index.jsx';
import { BreakpointMobile } from '../../constants/ui';
import { MetricChartHeader, MetricChartTooltip } from './index.jsx';
import { ChartColorsMap } from '../Charts/constants/index.jsx';

const ChartTooltip = ({ active, payload, sensor }) => {
  if (!active || !payload || payload.length < 1) return null;
  const data = payload[0].payload;
  const t = Number(data.value).toFixed(1);
  const valueCompared =
    payload.length > 2
      ? [
          {
            name: sensor.name,
            value: `${data.value}°C`,
            color: ChartColorsMap.soil,
          },
          {
            name: sensor.name2,
            value: `${data.value2}°C`,
            color: ChartColorsMap.soilCompared,
          },
        ]
      : null;
  return t !== null && <MetricChartTooltip value={`${t > 0 ? '+' + t : t}°C Avg`} comparedData={valueCompared} />;
};

const MetricChartSoilTemperature = ({ title, data, crop, sensor, widthViewport }) => {
  const { crops } = useSelector((state) => state.catalog);
  const { selectedSecondarySensor } = useSelector((state) => state.sensors);

  const [domainYAsis, setDomainYAsis] = useState(null);
  const [ticksYAsis, setTicksYAsis] = useState(null);

  useEffect(() => {
    if (data) {
      const domain = data.domain;
      if (domain && domain.length > 1) {
        setDomainYAsis(data.domain);
        const ticks = [data.domain[0], Math.round((data.domain[1] + data.domain[0]) / 2), data.domain[1]]
        setTicksYAsis([...new Set(ticks)]);
      }
    }
  }, [crops, crop, data]);

  if (!data) {
    return null;
  }

  return (
    <div className={styles.metricChart}>
      {widthViewport > BreakpointMobile && (
        <MetricChartHeader
          title={title}
          sensorName={sensor.name}
          sensorNameCompared={sensor.name2}
          color={ChartColorsMap.soil}
          colorCompared={ChartColorsMap.soilCompared}
          widthViewport={widthViewport}
          showDeep={true}
        />
      )}

      <ResponsiveContainer width="100%" height={140} className="chart-report">
        <ComposedChart
          width={500}
          height={140}
          data={data.data}
          margin={{
            top: 8,
            right: 8,
            left: 0,
            bottom: 0,
          }}
        >
          <Area
            dataKey="area"
            type="monotone"
            stroke="none"
            fillOpacity={0.03}
            fill="#000"
            activeDot={MetricChartDotSmall}
          />
          <CartesianGrid strokeDasharray="0 0" stroke={'var(--neutral-8)'} vertical={false} />
          <XAxis dataKey="date" tickLine={false} height={MetricChartXAxisHeight} />
          <YAxis
            domain={domainYAsis}
            ticks={ticksYAsis}
            tickCount={3}
            tickSize={0}
            tickMargin={6}
            width={MetricChartYAxisWidth}
            tickFormatter={(value) => (value > 0 ? `+${value}` : `${value}`)}
          />
          <Tooltip content={<ChartTooltip sensor={sensor} />} />
          <Line
            type="monotone"
            dataKey="value"
            strokeWidth={2}
            stroke={ChartColorsMap.soil}
            activeDot={{ r: MetricChartDotRadius, fill: '#fff', stroke: ChartColorsMap.soil }}
          />
          {selectedSecondarySensor && (data.data[0].value2 || data.data[0].value2 === 0) && (
            <Line
              type="monotone"
              dataKey="value2"
              strokeWidth={2}
              stroke={ChartColorsMap.soilCompared}
              strokeDasharray="2 2"
              activeDot={{ r: MetricChartDotRadius, fill: '#fff', stroke: ChartColorsMap.soilCompared }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      <div className={styles.metricChartUnit}>°C</div>
    </div>
  );
};

export default MetricChartSoilTemperature;
